import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle2, MapPin, Calendar, ChevronLeft, ChevronRight, Paperclip, FileText, DollarSign, Upload, CreditCard, User, Home, AlertCircle, Phone, Building, ExternalLink, MessageSquare, Bell, Route, Download, Navigation, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function TaskList(props) {
  var tasks = props.tasks || [];
  var onStatusUpdate = props.onStatusUpdate;
  var onRefresh = props.onRefresh;
  var employeeId = props.employeeId;
  
  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-400">No tasks yet</p>
      </div>
    );
  }

  var taskItems = [];
  for (var i = 0; i < tasks.length; i++) {
    var t = tasks[i];
    taskItems.push(<TaskCard key={t.id} task={t} onStatusUpdate={onStatusUpdate} onRefresh={onRefresh} employeeId={employeeId} />);
  }

  return <div className="space-y-4">{taskItems}</div>;
}

export function TaskCard(props) {
  var task = props.task;
  var onStatusUpdate = props.onStatusUpdate;
  var onRefresh = props.onRefresh;
  var employeeId = props.employeeId;
  var [showStatusModal, setShowStatusModal] = useState(false);
  var [selectedStatus, setSelectedStatus] = useState('');
  var [statusComment, setStatusComment] = useState('');
  
  var priorityClass = 'bg-blue-500/20 text-blue-300';
  var priorityRate = 60;
  if (task.priority === 'urgent') { priorityClass = 'bg-red-500/20 text-red-300'; priorityRate = 80; }
  if (task.priority === 'high') { priorityClass = 'bg-orange-500/20 text-orange-300'; priorityRate = 70; }
  if (task.priority === 'low') { priorityRate = 50; }
  
  var statusClass = 'bg-yellow-500/20 text-yellow-300';
  if (task.status === 'completed') statusClass = 'bg-green-500/20 text-green-300';
  if (task.status === 'in_progress') statusClass = 'bg-blue-500/20 text-blue-300';
  if (task.status === 'processing') statusClass = 'bg-purple-500/20 text-purple-300';
  
  var statusLabel = task.status ? task.status.replace('_', ' ') : 'pending';
  var attachmentList = task.attachments || [];
  var hasBusinessInfo = task.business_name || task.contact_number;
  
  var procedureLabels = {
    'first_call': 'First Time Call',
    'follow_up_docs': 'Follow Up Documents',
    'menu_collection': 'Menu Collection',
    'onboard': 'Direct Onboarding'
  };

  function handleStatusChange(newStatus) {
    if (newStatus === 'completed') {
      onStatusUpdate(task.id, newStatus, statusComment);
      setShowStatusModal(false);
    } else if (newStatus === 'pending' || newStatus === 'processing') {
      setSelectedStatus(newStatus);
      setShowStatusModal(true);
    } else {
      onStatusUpdate(task.id, newStatus, null);
    }
  }

  function handleSubmitStatus() {
    onStatusUpdate(task.id, selectedStatus, statusComment);
    setShowStatusModal(false);
    setStatusComment('');
  }

  function openAdminPanel() {
    var adminUrl = API_URL;
    if (task.business_type === 'restaurant') {
      adminUrl += '/admin?section=vendors&action=add&name=' + encodeURIComponent(task.business_name || '') + '&phone=' + encodeURIComponent(task.contact_number || '');
    } else {
      adminUrl += '/admin?section=delivery&action=add&name=' + encodeURIComponent(task.business_name || '') + '&phone=' + encodeURIComponent(task.contact_number || '');
    }
    window.open(adminUrl, '_blank');
  }

  return (
    <div className="p-4 bg-slate-700/30 rounded-lg" data-testid={'task-' + task.id}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-white font-medium">{task.title}</h4>
          {task.procedure_type && (
            <Badge className="bg-purple-500/20 text-purple-300 mt-1">{procedureLabels[task.procedure_type] || task.procedure_type}</Badge>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-green-400 font-medium">₹{priorityRate}</span>
          <Badge className={priorityClass}>{task.priority}</Badge>
          <Badge className={statusClass}>{statusLabel}</Badge>
        </div>
      </div>

      {hasBusinessInfo && (
        <div className="p-3 bg-slate-600/30 rounded-lg mb-3">
          <div className="flex items-start justify-between">
            <div>
              {task.business_name && (
                <div className="flex items-center gap-2 mb-1">
                  <Building className="w-4 h-4 text-amber-400" />
                  <span className="text-white font-medium">{task.business_name}</span>
                  <Badge className={task.business_type === 'restaurant' ? 'bg-orange-500/20 text-orange-300 text-xs' : 'bg-blue-500/20 text-blue-300 text-xs'}>
                    {task.business_type === 'restaurant' ? 'Restaurant' : 'Delivery Partner'}
                  </Badge>
                </div>
              )}
              {task.business_location && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <MapPin className="w-3 h-3" />
                  <span>{task.business_location}</span>
                </div>
              )}
              {task.contact_number && (
                <div className="flex items-center gap-2 mt-1">
                  <Phone className="w-3 h-3 text-green-400" />
                  <a href={'tel:' + task.contact_number} className="text-green-400 hover:underline">{task.contact_number}</a>
                </div>
              )}
            </div>
            {!task.onboarded && task.status !== 'completed' && (
              <Button size="sm" onClick={openAdminPanel} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-xs">
                <ExternalLink className="w-3 h-3 mr-1" />Onboard
              </Button>
            )}
            {task.onboarded && (
              <Badge className="bg-green-500/20 text-green-300">Onboarded</Badge>
            )}
          </div>
        </div>
      )}

      {task.description && <p className="text-sm text-gray-400 mb-3">{task.description}</p>}
      
      {task.status_comment && (
        <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded mb-3">
          <p className="text-xs text-amber-300 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {task.status_comment}
          </p>
        </div>
      )}

      {attachmentList.length > 0 && <AttachmentsList attachments={attachmentList} />}
      
      <div className="flex items-center justify-between mt-4">
        <p className="text-xs text-gray-400">Due: {new Date(task.due_date).toLocaleDateString()}</p>
        {task.status !== 'completed' && (
          <div className="flex gap-2">
            <Select onValueChange={handleStatusChange}>
              <SelectTrigger className="bg-slate-600 border-white/10 text-white w-32 h-8 text-xs">
                <SelectValue placeholder="Update Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="bg-slate-800 border-white/10 text-white">
          <DialogHeader><DialogTitle>Add Comment for {selectedStatus.replace('_', ' ')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Please add a comment explaining why this task is {selectedStatus}:</p>
            <Textarea 
              placeholder="Enter your comment..." 
              value={statusComment} 
              onChange={function(e) { setStatusComment(e.target.value); }} 
              className="bg-slate-700/50 border-white/10 text-white" 
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowStatusModal(false); }} className="border-white/20 text-white">Cancel</Button>
            <Button onClick={handleSubmitStatus} className="bg-purple-600 hover:bg-purple-700">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AttachmentsList(props) {
  var attachments = props.attachments || [];
  var attItems = [];
  for (var i = 0; i < attachments.length; i++) {
    var att = attachments[i];
    attItems.push(
      <a key={att.id} href={API_URL + att.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-slate-700 px-2 py-1 rounded text-xs text-blue-300 hover:underline">
        <FileText className="w-3 h-3" />{att.filename}
      </a>
    );
  }
  return (
    <div className="mb-3 p-2 bg-slate-600/30 rounded">
      <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> {attachments.length} attachment(s)
      </p>
      <div className="flex flex-wrap gap-2">{attItems}</div>
    </div>
  );
}

export function WeeklyScheduleView(props) {
  var schedule = props.schedule;
  var weekStart = props.weekStart;
  var onNavigate = props.onNavigate;
  var onCancel = props.onCancel;

  if (!schedule) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6 text-center text-gray-400">Loading schedule...</CardContent>
      </Card>
    );
  }

  var dates = Object.keys(schedule);
  var dayCells = [];
  for (var i = 0; i < dates.length; i++) {
    var date = dates[i];
    var day = schedule[date];
    dayCells.push(<DayCell key={date} date={date} day={day} onCancel={onCancel} />);
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-400" />Weekly Schedule</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={function() { onNavigate(-1); }} className="text-gray-400"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-white text-sm">{weekStart ? new Date(weekStart).toLocaleDateString() : ''}</span>
          <Button variant="ghost" size="sm" onClick={function() { onNavigate(1); }} className="text-gray-400"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-2">{dayCells}</div>
      </CardContent>
    </Card>
  );
}

export function DayCell(props) {
  var date = props.date;
  var day = props.day;
  var onCancel = props.onCancel;
  
  var slots = day.slots || [];
  var hasSlots = slots.length > 0;
  var hasAttendance = day.attendance;
  var bgClass = (hasSlots || hasAttendance) ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-slate-700/30';

  var slotItems = [];
  for (var i = 0; i < slots.length; i++) {
    var s = slots[i];
    slotItems.push(
      <div key={s.id} className="mt-2">
        <p className="text-xs text-amber-300">{s.label || (s.start_time + '-' + s.end_time)}</p>
        <Button size="sm" variant="ghost" onClick={function() { onCancel(s.id); }} className="text-xs text-red-400 p-0 h-auto mt-1">Cancel</Button>
      </div>
    );
  }

  var attendanceDisplay = null;
  if (hasAttendance) {
    var attStatus = day.attendance.status;
    var attHours = day.attendance.hours_worked;
    var attClass = attStatus === 'completed' ? 'bg-green-500/20 text-green-300 text-xs' : 'bg-blue-500/20 text-blue-300 text-xs';
    var attText = attHours ? attHours.toFixed(1) + 'h' : 'Working';
    attendanceDisplay = <div className="mt-2"><Badge className={attClass}>{attText}</Badge></div>;
  }

  return (
    <div className={'p-3 rounded-lg text-center ' + bgClass}>
      <p className="text-xs text-gray-400">{day.day_name.substring(0, 3)}</p>
      <p className="text-sm text-white font-medium">{new Date(date).getDate()}</p>
      {slotItems}
      {attendanceDisplay}
    </div>
  );
}

export function AvailableSlotsView(props) {
  var slots = props.slots || [];
  var employeeId = props.employeeId;
  var onBook = props.onBook;
  var selectedDate = props.selectedDate;
  var onDateChange = props.onDateChange;

  var slotCards = [];
  for (var i = 0; i < slots.length; i++) {
    var slot = slots[i];
    slotCards.push(<SlotCard key={slot.id} slot={slot} employeeId={employeeId} onBook={onBook} />);
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white">Available Slots to Book</CardTitle>
        <p className="text-sm text-amber-400 mt-1">Slots must be booked at least 24 hours in advance</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {slotCards}
          {slots.length === 0 && <p className="text-center text-gray-400 py-8 col-span-full">No slots available for booking (24+ hours away)</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function SlotCard(props) {
  var slot = props.slot;
  var employeeId = props.employeeId;
  var onBook = props.onBook;
  var bookedBy = slot.booked_by || [];
  var isBooked = bookedBy.indexOf(employeeId) !== -1;
  var spotsLeft = slot.max_bookings - slot.current_bookings;

  return (
    <div className="p-4 bg-slate-700/30 rounded-lg">
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="text-white font-medium">{slot.label || (slot.start_time + ' - ' + slot.end_time)}</p>
          <p className="text-sm text-gray-400">{new Date(slot.date).toLocaleDateString()}</p>
        </div>
        <Badge className={spotsLeft < 2 ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}>
          {spotsLeft} spots
        </Badge>
      </div>
      <Button size="sm" onClick={function() { onBook(slot.id); }} className="w-full bg-amber-600 hover:bg-amber-700 mt-2" disabled={isBooked || spotsLeft === 0}>
        {isBooked ? 'Already Booked' : spotsLeft === 0 ? 'Full' : 'Book Slot'}
      </Button>
    </div>
  );
}

export function LocationView(props) {
  var currentLocation = props.currentLocation;
  var isCheckedIn = props.isCheckedIn;

  var content;
  if (currentLocation) {
    content = (
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-sm text-gray-400">Latitude</p><p className="text-xl font-mono text-white">{currentLocation.latitude.toFixed(6)}</p></div>
          <div><p className="text-sm text-gray-400">Longitude</p><p className="text-xl font-mono text-white">{currentLocation.longitude.toFixed(6)}</p></div>
        </div>
      </div>
    );
  } else {
    var locationMsg = isCheckedIn ? 'Acquiring location...' : 'Check in to start tracking';
    content = (
      <div className="text-center py-12">
        <MapPin className="w-12 h-12 text-gray-500 mx-auto mb-4" />
        <p className="text-gray-400">{locationMsg}</p>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader><CardTitle className="text-white flex items-center gap-2"><MapPin className="w-5 h-5 text-blue-400" />My Location</CardTitle></CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

export function AttendanceHistoryView(props) {
  var records = props.records || [];

  var recordItems = [];
  for (var i = 0; i < records.length; i++) {
    var record = records[i];
    var checkOutTime = record.check_out ? new Date(record.check_out).toLocaleTimeString() : 'In progress';
    var hoursText = record.hours_worked ? record.hours_worked.toFixed(2) : '--';
    var statusBadgeClass = record.status === 'completed' ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300';
    recordItems.push(
      <div key={i} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
        <div>
          <p className="text-white font-medium">{new Date(record.date).toLocaleDateString()}</p>
          <p className="text-sm text-gray-400">{new Date(record.check_in).toLocaleTimeString()} - {checkOutTime}</p>
        </div>
        <div className="text-right">
          <p className="text-white font-medium">{hoursText}h</p>
          <Badge className={statusBadgeClass}>{record.status}</Badge>
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader><CardTitle className="text-white">Attendance History</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {recordItems}
          {records.length === 0 && <p className="text-center text-gray-400 py-8">No attendance records</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// NEW: Earnings View for Flex Employees
export function EarningsView(props) {
  var employeeId = props.employeeId;
  var [earnings, setEarnings] = useState(null);
  var [dailyProgress, setDailyProgress] = useState(null);
  var [badges, setBadges] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (employeeId) {
      Promise.all([
        fetch(API_URL + '/api/employee/earnings/' + employeeId).then(function(r) { return r.json(); }),
        fetch(API_URL + '/api/employee/daily-progress/' + employeeId).then(function(r) { return r.json(); }),
        fetch(API_URL + '/api/employee/badges/' + employeeId).then(function(r) { return r.json(); })
      ]).then(function(results) {
        setEarnings(results[0]);
        setDailyProgress(results[1]);
        setBadges(results[2]);
        setLoading(false);
      }).catch(function() { setLoading(false); });
    }
  }, [employeeId]);

  if (loading) return <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6 text-center text-gray-400">Loading...</CardContent></Card>;
  if (!earnings) return null;

  var badgeCounts = badges ? badges.badge_counts : { bronze: 0, silver: 0, gold: 0 };

  return (
    <div className="space-y-6">
      {/* Daily Progress Card */}
      {dailyProgress && (
        <Card className="bg-gradient-to-br from-slate-800/50 to-purple-900/20 border-white/10">
          <CardHeader><CardTitle className="text-white flex items-center gap-2">Today's Progress</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-4xl font-bold text-white">{dailyProgress.tasks_completed_today}</p>
                <p className="text-sm text-gray-400">tasks completed today</p>
              </div>
              {dailyProgress.current_tier && (
                <div className={'p-3 rounded-xl ' + getBadgeBgClass(dailyProgress.current_tier.badge)}>
                  {getBadgeIcon(dailyProgress.current_tier.badge)}
                  <p className="text-xs text-center mt-1 text-white">{dailyProgress.current_tier.badge_label}</p>
                </div>
              )}
            </div>
            
            {dailyProgress.next_tier && (
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Next: {dailyProgress.next_tier.badge_label}</span>
                  <span className="text-white">{dailyProgress.tasks_to_next_tier} tasks to go</span>
                </div>
                <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div className={'h-full rounded-full transition-all duration-500 ' + getBadgeGradient(dailyProgress.next_tier.badge)} style={{ width: dailyProgress.progress_percentage + '%' }}></div>
                </div>
              </div>
            )}

            {dailyProgress.total_bonus_today > 0 && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-medium">Bonus earned today: ₹{dailyProgress.total_bonus_today}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mt-4">
              {BONUS_TIERS.map(function(tier) {
                var isAchieved = dailyProgress.tasks_completed_today >= tier.tasks;
                return (
                  <div key={tier.badge} className={'p-3 rounded-lg text-center ' + (isAchieved ? getBadgeBgClass(tier.badge) : 'bg-slate-700/30 opacity-50')}>
                    {getBadgeIcon(tier.badge)}
                    <p className="text-xs text-white mt-1">{tier.tasks} tasks</p>
                    <p className="text-sm font-bold text-green-400">₹{tier.bonus}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Badges Collection */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardHeader><CardTitle className="text-white flex items-center gap-2">My Badges</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-gradient-to-br from-amber-700/30 to-amber-900/30 border border-amber-500/30 rounded-xl text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center">
                <span className="text-2xl">🥉</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{badgeCounts.bronze}</p>
              <p className="text-xs text-gray-400">Bronze</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-slate-400/30 to-slate-600/30 border border-slate-400/30 rounded-xl text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-slate-300 to-slate-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">🥈</span>
              </div>
              <p className="text-2xl font-bold text-slate-300">{badgeCounts.silver}</p>
              <p className="text-xs text-gray-400">Silver</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-yellow-500/30 to-yellow-700/30 border border-yellow-500/30 rounded-xl text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-2xl">🥇</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{badgeCounts.gold}</p>
              <p className="text-xs text-gray-400">Gold</p>
            </div>
          </div>
          
          {badges && badges.total_bonus_earned > 0 && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
              <p className="text-sm text-gray-400">Total Bonus Earned</p>
              <p className="text-2xl font-bold text-green-400">₹{badges.total_bonus_earned}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regular Earnings Card */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" />Task Earnings</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-slate-700/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-white">₹{earnings.total_earned}</p>
              <p className="text-xs text-gray-400">Task Earnings</p>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-400">₹{earnings.pending_approval}</p>
              <p className="text-xs text-gray-400">Pending Approval</p>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-400">₹{earnings.pending_payout}</p>
              <p className="text-xs text-gray-400">Ready for Payout</p>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-400">₹{earnings.paid_out}</p>
              <p className="text-xs text-gray-400">Paid Out</p>
            </div>
          </div>

          {!earnings.kyc_complete && earnings.pending_payout > 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <span className="text-amber-300">Complete your KYC to receive payouts</span>
              </div>
            </div>
          )}

          <div className="p-4 bg-slate-700/30 rounded-lg">
            <p className="text-sm text-gray-400 mb-2">Payment Schedule</p>
            <p className="text-white">Weekly payouts on <span className="text-amber-400 font-medium">Tuesdays after 12pm</span></p>
            <p className="text-sm text-gray-400 mt-2">Next payout: {new Date(earnings.next_payout_date).toLocaleDateString()}</p>
          </div>

          <div className="mt-4 p-4 bg-slate-700/30 rounded-lg">
            <p className="text-sm text-gray-400 mb-2">Payment Rates per Task</p>
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div><Badge className="bg-blue-500/20 text-blue-300">Low</Badge><p className="text-white mt-1">₹50</p></div>
              <div><Badge className="bg-blue-500/20 text-blue-300">Medium</Badge><p className="text-white mt-1">₹60</p></div>
              <div><Badge className="bg-orange-500/20 text-orange-300">High</Badge><p className="text-white mt-1">₹70</p></div>
              <div><Badge className="bg-red-500/20 text-red-300">Urgent</Badge><p className="text-white mt-1">₹80</p></div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

var BONUS_TIERS = [
  { tasks: 50, bonus: 200, badge: 'bronze' },
  { tasks: 80, bonus: 500, badge: 'silver' },
  { tasks: 150, bonus: 1000, badge: 'gold' }
];

function getBadgeBgClass(badge) {
  if (badge === 'gold') return 'bg-gradient-to-br from-yellow-500/30 to-yellow-700/30 border border-yellow-500/30';
  if (badge === 'silver') return 'bg-gradient-to-br from-slate-400/30 to-slate-600/30 border border-slate-400/30';
  return 'bg-gradient-to-br from-amber-700/30 to-amber-900/30 border border-amber-500/30';
}

function getBadgeGradient(badge) {
  if (badge === 'gold') return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
  if (badge === 'silver') return 'bg-gradient-to-r from-slate-400 to-slate-500';
  return 'bg-gradient-to-r from-amber-600 to-amber-700';
}

function getBadgeIcon(badge) {
  if (badge === 'gold') return <div className="w-10 h-10 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center"><span className="text-xl">🥇</span></div>;
  if (badge === 'silver') return <div className="w-10 h-10 mx-auto bg-gradient-to-br from-slate-300 to-slate-500 rounded-full flex items-center justify-center"><span className="text-xl">🥈</span></div>;
  return <div className="w-10 h-10 mx-auto bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center"><span className="text-xl">🥉</span></div>;
}

// NEW: KYC/Profile View for All Employees
export function KYCProfileView(props) {
  var employeeId = props.employeeId;
  var employeeType = props.employeeType;
  var [kyc, setKyc] = useState(null);
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [formData, setFormData] = useState({
    aadhaar_number: '',
    pan_number: '',
    current_address: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    account_holder_name: ''
  });

  useEffect(function() {
    if (employeeId) { fetchKYC(); }
  }, [employeeId]);

  function fetchKYC() {
    fetch(API_URL + '/api/employee/kyc/' + employeeId)
      .then(function(r) { return r.json(); })
      .then(function(d) { 
        setKyc(d); 
        if (d.kyc) {
          setFormData({
            aadhaar_number: d.kyc.aadhaar_number || '',
            pan_number: d.kyc.pan_number || '',
            current_address: d.kyc.current_address || '',
            bank_account_number: d.kyc.bank_account_number || '',
            bank_ifsc: d.kyc.bank_ifsc || '',
            bank_name: d.kyc.bank_name || '',
            account_holder_name: d.kyc.account_holder_name || ''
          });
        }
        setLoading(false); 
      })
      .catch(function() { setLoading(false); });
  }

  function handleSave() {
    fetch(API_URL + '/api/employee/kyc/' + employeeId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(function(r) { return r.json(); })
      .then(function() { toast.success('Details saved!'); setShowModal(false); fetchKYC(); })
      .catch(function() { toast.error('Failed to save'); });
  }

  function handleUpload(docType, file) {
    setUploading(true);
    var formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('doc_type', docType);
    
    fetch(API_URL + '/api/employee/kyc/' + employeeId + '/upload', {
      method: 'POST',
      body: formDataUpload
    })
      .then(function(r) { if (!r.ok) throw new Error('Upload failed'); return r.json(); })
      .then(function() { toast.success(docType + ' uploaded!'); fetchKYC(); })
      .catch(function(e) { toast.error(e.message); })
      .finally(function() { setUploading(false); });
  }

  if (loading) return <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6 text-center text-gray-400">Loading...</CardContent></Card>;

  var kycData = kyc ? kyc.kyc : {};
  var isComplete = kyc && kyc.kyc_complete;

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2"><User className="w-5 h-5 text-purple-400" />KYC & Profile</CardTitle>
        <Badge className={isComplete ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}>
          {isComplete ? 'Complete' : 'Incomplete'}
        </Badge>
      </CardHeader>
      <CardContent>
        {!isComplete && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
            <p className="text-amber-300 text-sm">
              {employeeType === 'flex_employee' 
                ? 'Complete your KYC to receive weekly payouts. Required: Aadhaar, Address, Bank Details, Selfie'
                : 'Complete your profile by uploading your documents. Required: Aadhaar, Address, Bank Details, Selfie'
              }
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-slate-700/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400 text-sm">Aadhaar Card</span>
            </div>
            {kycData.aadhaar_url ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm">Uploaded</span>
                <a href={kycData.aadhaar_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline">View</a>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                  <Upload className="w-4 h-4" /><span className="text-sm">Upload</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={function(e) { if (e.target.files[0]) handleUpload('aadhaar', e.target.files[0]); }} disabled={uploading} />
              </label>
            )}
            {kycData.aadhaar_number && <p className="text-white text-sm mt-2">****{kycData.aadhaar_number.slice(-4)}</p>}
          </div>

          <div className="p-4 bg-slate-700/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400 text-sm">PAN Card (Optional)</span>
            </div>
            {kycData.pan_url ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm">Uploaded</span>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 text-gray-400 hover:text-gray-300">
                  <Upload className="w-4 h-4" /><span className="text-sm">Upload</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={function(e) { if (e.target.files[0]) handleUpload('pan', e.target.files[0]); }} disabled={uploading} />
              </label>
            )}
          </div>

          <div className="p-4 bg-slate-700/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-green-400" />
              <span className="text-gray-400 text-sm">Selfie</span>
            </div>
            {kycData.selfie_url ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm">Uploaded</span>
                <a href={kycData.selfie_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline">View</a>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                  <Upload className="w-4 h-4" /><span className="text-sm">Upload</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={function(e) { if (e.target.files[0]) handleUpload('selfie', e.target.files[0]); }} disabled={uploading} />
              </label>
            )}
          </div>

          <div className="p-4 bg-slate-700/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Home className="w-4 h-4 text-amber-400" />
              <span className="text-gray-400 text-sm">Address</span>
            </div>
            {kycData.current_address ? (
              <p className="text-white text-sm">{kycData.current_address.substring(0, 50)}...</p>
            ) : (
              <p className="text-amber-400 text-sm">Not provided</p>
            )}
          </div>
        </div>

        <div className="p-4 bg-slate-700/30 rounded-lg mb-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-green-400" />
            <span className="text-gray-400 text-sm">Bank Account Details</span>
          </div>
          {kycData.bank_account_number ? (
            <div className="text-white text-sm">
              <p>{kycData.bank_name || 'Bank'} - ****{kycData.bank_account_number.slice(-4)}</p>
              <p className="text-gray-400">IFSC: {kycData.bank_ifsc || 'N/A'}</p>
            </div>
          ) : (
            <p className="text-amber-400 text-sm">Not provided</p>
          )}
        </div>

        <Button onClick={function() { setShowModal(true); }} className="w-full bg-purple-600 hover:bg-purple-700">Edit Details</Button>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-slate-800 border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle>Edit KYC Details</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div>
                <label className="text-sm text-gray-400">Aadhaar Number</label>
                <Input value={formData.aadhaar_number} onChange={function(e) { setFormData(Object.assign({}, formData, { aadhaar_number: e.target.value })); }} placeholder="12 digit Aadhaar" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">PAN Number (Optional)</label>
                <Input value={formData.pan_number} onChange={function(e) { setFormData(Object.assign({}, formData, { pan_number: e.target.value })); }} placeholder="ABCDE1234F" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">Current Address</label>
                <Input value={formData.current_address} onChange={function(e) { setFormData(Object.assign({}, formData, { current_address: e.target.value })); }} placeholder="Full address" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">Account Holder Name</label>
                <Input value={formData.account_holder_name} onChange={function(e) { setFormData(Object.assign({}, formData, { account_holder_name: e.target.value })); }} placeholder="Name as per bank" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">Bank Name</label>
                <Input value={formData.bank_name} onChange={function(e) { setFormData(Object.assign({}, formData, { bank_name: e.target.value })); }} placeholder="Bank name" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">Account Number</label>
                <Input value={formData.bank_account_number} onChange={function(e) { setFormData(Object.assign({}, formData, { bank_account_number: e.target.value })); }} placeholder="Account number" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">IFSC Code</label>
                <Input value={formData.bank_ifsc} onChange={function(e) { setFormData(Object.assign({}, formData, { bank_ifsc: e.target.value })); }} placeholder="IFSC code" className="bg-slate-700/50 border-white/10 text-white" />
              </div>
            </div>
            <DialogFooter><Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}


// ==================== FLEET MANAGER COMPONENTS ====================

// Fleet Manager Distance View - Shows today's km and history
export function FleetDistanceView(props) {
  var employeeId = props.employeeId;
  var [todayKm, setTodayKm] = useState(0);
  var [distanceHistory, setDistanceHistory] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (employeeId) {
      Promise.all([
        fetch(API_URL + '/api/employee/daily-distance/' + employeeId).then(function(r) { return r.json(); }),
        fetch(API_URL + '/api/employee/distance-history/' + employeeId + '?days=30').then(function(r) { return r.json(); })
      ]).then(function(results) {
        setTodayKm(results[0].total_km || 0);
        setDistanceHistory(results[1].history || []);
        setLoading(false);
      }).catch(function() { setLoading(false); });
    }
  }, [employeeId]);

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6 text-center text-gray-400">Loading distance data...</CardContent>
      </Card>
    );
  }

  var historyItems = [];
  for (var i = 0; i < distanceHistory.length; i++) {
    var record = distanceHistory[i];
    historyItems.push(
      <div key={record.date} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
        <span className="text-white">{record.date}</span>
        <span className="text-blue-400 font-bold">{record.total_km.toFixed(2)} km</span>
      </div>
    );
  }

  var totalKm = distanceHistory.reduce(function(sum, r) { return sum + (r.total_km || 0); }, 0);

  return (
    <div className="space-y-6">
      {/* Today's Distance Card */}
      <Card className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            Today's Distance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-5xl font-bold text-blue-400">{todayKm.toFixed(2)}</p>
            <p className="text-gray-400 text-lg mt-2">kilometres driven</p>
          </div>
        </CardContent>
      </Card>

      {/* Distance History */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Distance History (Last 30 Days)</span>
            <Badge className="bg-blue-500/20 text-blue-300">Total: {totalKm.toFixed(2)} km</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {historyItems}
            {distanceHistory.length === 0 && (
              <p className="text-center text-gray-400 py-8">No distance records yet. Start driving to track your kilometres.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Fleet Manager Badges View - Shows weekly badges assigned by admin
export function FleetBadgesView(props) {
  var employeeId = props.employeeId;
  var weeklyBadge = props.weeklyBadge;

  var badgeDisplay = null;
  if (weeklyBadge) {
    var badgeClass = '';
    var badgeEmoji = '';
    var badgeGradient = '';
    
    if (weeklyBadge.badge_type === 'gold') {
      badgeClass = 'border-yellow-500/50';
      badgeEmoji = '🥇';
      badgeGradient = 'from-yellow-500/30 to-yellow-700/30';
    } else if (weeklyBadge.badge_type === 'silver') {
      badgeClass = 'border-slate-400/50';
      badgeEmoji = '🥈';
      badgeGradient = 'from-slate-400/30 to-slate-600/30';
    } else {
      badgeClass = 'border-amber-600/50';
      badgeEmoji = '🥉';
      badgeGradient = 'from-amber-700/30 to-amber-900/30';
    }

    badgeDisplay = (
      <div className={'p-6 bg-gradient-to-br ' + badgeGradient + ' border ' + badgeClass + ' rounded-xl text-center'}>
        <div className="text-6xl mb-4">{badgeEmoji}</div>
        <p className="text-2xl font-bold text-white capitalize">{weeklyBadge.badge_type} Badge</p>
        <p className="text-gray-300 mt-2">{weeklyBadge.badge_label || 'Weekly Achievement'}</p>
        {weeklyBadge.reason && <p className="text-sm text-gray-400 mt-2">{weeklyBadge.reason}</p>}
        <p className="text-xs text-gray-500 mt-4">Week of {weeklyBadge.week_start}</p>
      </div>
    );
  } else {
    badgeDisplay = (
      <div className="p-8 bg-slate-700/30 rounded-xl text-center">
        <div className="text-5xl mb-4 opacity-30">🏆</div>
        <p className="text-gray-400">No badge assigned for this week yet</p>
        <p className="text-sm text-gray-500 mt-2">Complete your tasks to earn recognition from your manager</p>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <span className="text-xl">🏅</span>
          Weekly Badge
        </CardTitle>
      </CardHeader>
      <CardContent>
        {badgeDisplay}
        
        <div className="mt-6 p-4 bg-slate-700/30 rounded-lg">
          <p className="text-sm text-gray-400 mb-3">Badge Tiers:</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2">
              <span className="text-2xl">🥉</span>
              <p className="text-xs text-amber-400 mt-1">Bronze</p>
              <p className="text-xs text-gray-500">Good</p>
            </div>
            <div className="text-center p-2">
              <span className="text-2xl">🥈</span>
              <p className="text-xs text-slate-300 mt-1">Silver</p>
              <p className="text-xs text-gray-500">Great</p>
            </div>
            <div className="text-center p-2">
              <span className="text-2xl">🥇</span>
              <p className="text-xs text-yellow-400 mt-1">Gold</p>
              <p className="text-xs text-gray-500">Excellent</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== NOTIFICATION BELL ====================

export function NotificationBell(props) {
  var employeeId = props.employeeId;
  var [notifications, setNotifications] = useState([]);
  var [unreadCount, setUnreadCount] = useState(0);
  var [showDropdown, setShowDropdown] = useState(false);
  var [loading, setLoading] = useState(false);
  var dropdownRef = useState(null)[0];

  useEffect(function() {
    if (employeeId) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      var interval = setInterval(fetchNotifications, 30000);
      return function() { clearInterval(interval); };
    }
  }, [employeeId]);

  // Close dropdown on outside click
  useEffect(function() {
    function handleClickOutside(event) {
      if (showDropdown) {
        var dropdown = document.querySelector('[data-notification-dropdown]');
        var bell = document.querySelector('[data-testid="notification-bell"]');
        if (dropdown && !dropdown.contains(event.target) && bell && !bell.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function() { document.removeEventListener('mousedown', handleClickOutside); };
  }, [showDropdown]);

  function fetchNotifications() {
    fetch(API_URL + '/api/employee/notifications/' + employeeId + '?limit=10')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setNotifications(d.notifications || []);
        setUnreadCount(d.unread_count || 0);
      })
      .catch(function() {});
  }

  function markAsRead(notificationId) {
    fetch(API_URL + '/api/employee/notifications/' + notificationId + '/read', { method: 'PUT' })
      .then(function() { fetchNotifications(); });
  }

  function markAllAsRead() {
    fetch(API_URL + '/api/employee/notifications/read-all/' + employeeId, { method: 'PUT' })
      .then(function() { fetchNotifications(); setShowDropdown(false); });
  }

  var notificationItems = [];
  for (var i = 0; i < notifications.length; i++) {
    var n = notifications[i];
    var bgClass = n.read ? 'bg-slate-700/30' : 'bg-blue-500/10 border-l-2 border-blue-500';
    notificationItems.push(
      <div 
        key={n.id} 
        className={'p-3 rounded-lg cursor-pointer hover:bg-slate-600/30 ' + bgClass}
        onClick={function(id) { return function() { markAsRead(id); }; }(n.id)}
      >
        <p className="text-white text-sm font-medium">{n.title}</p>
        <p className="text-gray-400 text-xs mt-1">{n.message}</p>
        <p className="text-gray-500 text-xs mt-2">{new Date(n.created_at).toLocaleString()}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={function() { setShowDropdown(!showDropdown); }}
        className="text-gray-400 hover:text-white relative"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {showDropdown && (
        <div data-notification-dropdown className="absolute right-0 top-12 w-80 bg-slate-800 border border-white/10 rounded-xl shadow-xl z-50">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-blue-400 text-xs">
                Mark all read
              </Button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto p-2 space-y-2">
            {notificationItems}
            {notifications.length === 0 && (
              <p className="text-center text-gray-400 py-4">No notifications</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ROUTE OPTIMIZATION ====================

export function RouteOptimizationView(props) {
  var employeeId = props.employeeId;
  var currentLocation = props.currentLocation;
  var [route, setRoute] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (employeeId) {
      fetchOptimizedRoute();
    }
  }, [employeeId, currentLocation]);

  function fetchOptimizedRoute() {
    setLoading(true);
    var url = API_URL + '/api/employee/optimized-route/' + employeeId;
    if (currentLocation) {
      url += '?current_lat=' + currentLocation.latitude + '&current_lng=' + currentLocation.longitude;
    }
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setRoute(d);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6 text-center text-gray-400">Loading optimized route...</CardContent>
      </Card>
    );
  }

  var routeItems = [];
  if (route && route.optimized_route) {
    for (var i = 0; i < route.optimized_route.length; i++) {
      var task = route.optimized_route[i];
      var priorityClass = task.priority === 'urgent' ? 'bg-red-500/20 text-red-300' : task.priority === 'high' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300';
      var statusClass = task.status === 'completed' ? 'bg-green-500/20 text-green-300' : task.status === 'processing' ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-300';
      
      routeItems.push(
        <div key={task.task_id} className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-lg">
          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
            {task.order}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-white font-medium">{task.title}</h4>
              <Badge className={priorityClass}>{task.priority}</Badge>
              <Badge className={statusClass}>{task.status}</Badge>
            </div>
            {task.business_name && (
              <p className="text-sm text-gray-300"><Building className="w-3 h-3 inline mr-1" />{task.business_name}</p>
            )}
            {task.business_location && (
              <p className="text-sm text-gray-400"><MapPin className="w-3 h-3 inline mr-1" />{task.business_location}</p>
            )}
            {task.contact_number && (
              <a href={'tel:' + task.contact_number} className="text-sm text-blue-400 hover:underline"><Phone className="w-3 h-3 inline mr-1" />{task.contact_number}</a>
            )}
            {task.distance_from_previous !== null && (
              <p className="text-xs text-green-400 mt-2"><Navigation className="w-3 h-3 inline mr-1" />{task.distance_from_previous} km from previous stop</p>
            )}
            {task.note && (
              <p className="text-xs text-amber-400 mt-1">{task.note}</p>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="space-y-6">
      {/* Route Summary */}
      <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-green-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Route className="w-5 h-5 text-green-400" />
            Optimized Route
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <p className="text-2xl font-bold text-white">{route ? route.total_tasks : 0}</p>
              <p className="text-xs text-gray-400">Total Stops</p>
            </div>
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <p className="text-2xl font-bold text-green-400">{route ? route.total_distance : 0} km</p>
              <p className="text-xs text-gray-400">Est. Distance</p>
            </div>
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <p className="text-2xl font-bold text-blue-400">{currentLocation ? 'Live' : 'Default'}</p>
              <p className="text-xs text-gray-400">Start Location</p>
            </div>
          </div>
          <p className="text-sm text-gray-400 mt-4 text-center">Tasks are ordered by nearest location first to minimize travel time</p>
        </CardContent>
      </Card>

      {/* Route Steps */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Route Steps</CardTitle>
          <Button onClick={fetchOptimizedRoute} variant="outline" size="sm" className="border-white/20 text-white">
            <Navigation className="w-4 h-4 mr-1" />Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {routeItems}
            {routeItems.length === 0 && (
              <p className="text-center text-gray-400 py-8">No pending tasks to route</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SALARY SLIPS ====================

export function SalarySlipsView(props) {
  var employeeId = props.employeeId;
  var [slips, setSlips] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (employeeId) {
      fetchSalarySlips();
    }
  }, [employeeId]);

  function fetchSalarySlips() {
    setLoading(true);
    fetch(API_URL + '/api/employee/salary-slips/' + employeeId)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSlips(d.salary_slips || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6 text-center text-gray-400">Loading salary slips...</CardContent>
      </Card>
    );
  }

  var slipItems = [];
  for (var i = 0; i < slips.length; i++) {
    var slip = slips[i];
    var monthDate = new Date(slip.month + '-01');
    var monthLabel = monthDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    
    slipItems.push(
      <div key={slip.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-white font-medium">{monthLabel}</p>
            <p className="text-sm text-gray-400">Uploaded: {new Date(slip.uploaded_at).toLocaleDateString()}</p>
            {slip.notes && <p className="text-xs text-gray-500 mt-1">{slip.notes}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-lg font-bold text-green-400">₹{slip.amount.toLocaleString()}</p>
            <p className="text-xs text-gray-400">Net Salary</p>
          </div>
          <a 
            href={slip.file_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Download className="w-5 h-5 text-white" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-400" />
          Salary Slips
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {slipItems}
          {slips.length === 0 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No salary slips available yet</p>
              <p className="text-sm text-gray-500 mt-1">Your salary slips will appear here once uploaded by admin</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Holiday Calendar View Component
export function HolidayCalendarView() {
  var [holidays, setHolidays] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  var [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(function() {
    loadHolidays();
  }, [selectedYear]);

  function loadHolidays() {
    setLoading(true);
    fetch(API_URL + '/api/employee/holidays?year=' + selectedYear)
      .then(function(res) { return res.json(); })
      .then(function(data) { 
        setHolidays(data.holidays || []); 
        setLoading(false); 
      })
      .catch(function() { 
        setLoading(false); 
      });
  }

  var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function isHoliday(day) {
    var dateStr = selectedYear + '-' + String(selectedMonth + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    return holidays.find(function(h) { return h.date === dateStr; });
  }

  function isToday(day) {
    var today = new Date();
    return today.getDate() === day && today.getMonth() === selectedMonth && today.getFullYear() === selectedYear;
  }

  function prevMonth() {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  }

  function nextMonth() {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  }

  var daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  var firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
  
  var calendarDays = [];
  // Empty cells for days before first day of month
  for (var i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={'empty-' + i} className="h-10"></div>);
  }
  // Days of the month
  for (var day = 1; day <= daysInMonth; day++) {
    var holiday = isHoliday(day);
    var todayClass = isToday(day) ? 'ring-2 ring-blue-500' : '';
    var holidayClass = holiday ? 'bg-red-500/20 text-red-300' : 'bg-slate-700/30 text-gray-300';
    var isSunday = (firstDay + day - 1) % 7 === 0;
    
    calendarDays.push(
      <div 
        key={'day-' + day} 
        className={'h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer hover:bg-slate-600/50 transition-colors ' + holidayClass + ' ' + todayClass + (isSunday ? ' text-red-400' : '')}
        title={holiday ? holiday.name : (isSunday ? 'Sunday' : '')}
      >
        {day}
        {holiday && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
      </div>
    );
  }

  // Get holidays for current month
  var monthHolidays = holidays.filter(function(h) {
    var parts = h.date.split('-');
    return parseInt(parts[1]) === selectedMonth + 1;
  });

  // Get upcoming holidays (next 3 months)
  var today = new Date();
  var upcomingHolidays = holidays.filter(function(h) {
    var holidayDate = new Date(h.date);
    return holidayDate >= today;
  }).slice(0, 5);

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-400" />
          Holiday Calendar {selectedYear}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="sm" onClick={prevMonth} className="text-gray-400 hover:text-white">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h3 className="text-lg font-semibold text-white">{monthNames[selectedMonth]} {selectedYear}</h3>
                <Button variant="ghost" size="sm" onClick={nextMonth} className="text-gray-400 hover:text-white">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(function(d, idx) {
                  return (
                    <div key={d} className={'text-center text-xs font-medium ' + (idx === 0 ? 'text-red-400' : 'text-gray-400')}>
                      {d}
                    </div>
                  );
                })}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays}
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500/20 rounded"></div>
                  <span>Holiday</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 ring-2 ring-blue-500 rounded"></div>
                  <span>Today</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-red-400">Sun</span>
                  <span>Weekly Off</span>
                </div>
              </div>
            </div>
            
            {/* Holiday List */}
            <div className="space-y-4">
              {/* This month's holidays */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">{monthNames[selectedMonth]} Holidays</h4>
                {monthHolidays.length > 0 ? (
                  <div className="space-y-2">
                    {monthHolidays.map(function(h) {
                      var date = new Date(h.date);
                      return (
                        <div key={h.id} className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <p className="text-white font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-gray-400">{date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
                          {h.type && <Badge className="mt-1 bg-red-600/50 text-xs">{h.type}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No holidays this month</p>
                )}
              </div>
              
              {/* Upcoming holidays */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Upcoming Holidays</h4>
                {upcomingHolidays.length > 0 ? (
                  <div className="space-y-2">
                    {upcomingHolidays.map(function(h) {
                      var date = new Date(h.date);
                      return (
                        <div key={h.id} className="p-2 bg-slate-700/30 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">{h.name}</p>
                            <p className="text-xs text-gray-400">{date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                          {h.type && <Badge className="bg-purple-600/50 text-xs">{h.type}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No upcoming holidays</p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
